import { useMyAppointmentsLocale } from '../pages/MyAppointments';
import {
  AppointmentHistoryRowProps,
  AppointmentStatus,
  HistoricAppointmentsFilter,
} from '../interfaces';
import {
  formatDateToCustomFormat,
  generateAppointmentLogs,
} from 'utilities/functions';
import {
  AppointmentTypeEnum,
  useGetAppointmentsByPatientCodexIdQuery,
  useGetAppointmentsByProviderQuery,
} from 'graphql/generated/remote-schema-hasura';

import {
  FHIR_APPOINTMENT_STATUS,
  ORDER_BY_DIRECTION_NUMERIC,
  SORT_FHIR_APPOINTMENT_BY,
} from 'utilities/interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LIST_BATCH_SIZE } from 'utilities/constants';
import { AppointmentEvent } from 'graphql/generated/hasura';
import { SIGNAL_CHANNELS, useChannelSignal } from 'hooks/useChannelSignal';
import { QueryContextType } from 'app/my-appointments/components/QueryContextType';
import React, { createContext, useCallback, useState } from 'react';
import { Roles } from '../../../firebase/interfaces';
import { AppointmentHasuraStatus } from '../const/appointment';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import { MY_APPOINTMENTS_DETAILS } from 'utilities/routes';

export const historyStatuses = [
  FHIR_APPOINTMENT_STATUS.FULFILLED,
  FHIR_APPOINTMENT_STATUS.NOSHOW,
  FHIR_APPOINTMENT_STATUS.CANCELLED,
];

type varsType = {
  limit: number;
  offset: number;
  filter: HistoricAppointmentsFilter;
};
type dataType = { appointments: AppointmentHistoryRowProps[]; total: number };
type AppointmentHistoryContextType = QueryContextType<
  AppointmentHistoryRowProps,
  varsType
>;

export const AppointmentHistoryContext =
  createContext<AppointmentHistoryContextType>(
    {} as unknown as AppointmentHistoryContextType,
  );

const PatientAppointmentHistoryProvider: React.FC<{
  children: React.ReactNode;
  offset?: number;
  patientCodexId?: string;
  handleMyVisitSummaryClick: (appointmentId: string) => void;
}> = ({ children, handleMyVisitSummaryClick, offset }) => {
  const { loading, locale } = useMyAppointmentsLocale();
  const { user: loggedUser } = useAuth();
  const [data, setData] = useState<dataType>({
    appointments: [],
    total: 0,
  });
  const [vars, setVars] = useState<varsType>({
    limit: 10,
    offset: offset || 0,
    filter: {
      sort: SORT_FHIR_APPOINTMENT_BY.DATE,
      sortDirection: ORDER_BY_DIRECTION_NUMERIC.DESC,
    },
  });
  const [allAmount, setAllAmount] = useState<number | undefined>(undefined);

  const { fetchFileFromStorage } = useGetFileFromStorage();
  const navigate = useNavigate();

  const getFormatedToDate = () => {
    if (vars.filter.to) {
      const toDate = new Date(vars.filter.to);
      toDate.setHours(23, 59, 59, 999);
      return toDate.toISOString();
    } else {
      return undefined;
    }
  };

  const getFormatedFromDate = () => {
    if (vars.filter.from) {
      const fromDate = new Date(vars.filter.from);
      fromDate.setHours(0, 0, 0, 0);
      return fromDate.toISOString();
    } else {
      return undefined;
    }
  };

  const handleDetailsClick = useCallback(
    (appointmentId?: string) => {
      if (!appointmentId) return;

      return navigate(`${MY_APPOINTMENTS_DETAILS}/${appointmentId}`, {
        state: { appointmentId },
      });
    },
    [navigate],
  );

  const getDownloadFunction = async (reportId?: string | null) => {
    if (!reportId) return;
    const pdfLink = await fetchFileFromStorage(reportId);
    window.open(pdfLink, '_blank');
  };

  const { loading: dataLoading, refetch } =
    useGetAppointmentsByPatientCodexIdQuery({
      variables: {
        to: getFormatedToDate(),
        from: getFormatedFromDate(),
        appointmentStatus: historyStatuses,
        ...vars.filter,
        providerName: vars.filter.provider,
        limit: vars.limit,
        offset: vars.offset,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        if (data.getFHIRAppointmentByRequestPatientCodexId.total > 0) {
          const allAppointmentHistory: AppointmentHistoryRowProps[] =
            data.getFHIRAppointmentByRequestPatientCodexId.appointment.map(
              (appointment): AppointmentHistoryRowProps => ({
                id: appointment.appointmentCodexId,
                date: appointment.start || '',
                name: `${appointment.providerFirstName} ${appointment.providerLastName}`,
                type: appointment.appointmentType || AppointmentTypeEnum.Chat,
                patientCodexId: appointment.codexPatientId,
                appointmentStatus:
                  appointment.appointmentStatus as AppointmentHasuraStatus,
                status: appointment.status as AppointmentStatus,
                providerCodexId: appointment.codexProviderId as string,
                diagnosticReports: appointment.diagnosticReports || [],
                logs: generateAppointmentLogs(
                  (appointment?.events ?? []) as AppointmentEvent[],
                  (locale?.events ?? {}) as unknown as Record<string, string>,
                ),
                actions: [
                  {
                    text: 'yourMyVisitSummary',
                    onClick: () =>
                      handleMyVisitSummaryClick(appointment.appointmentCodexId),
                  },
                  {
                    text: 'testKit',
                    onClick: (fileId?: string) => getDownloadFunction(fileId),
                  },
                  {
                    text: 'details',
                    onClick: handleDetailsClick,
                  },
                ],
                visitSummaryPdfUrl: appointment?.visitSummaryPdfUrl,
                summaryCompleted: appointment.isSummaryCompleted,
              }),
            );
          setData({
            appointments: allAppointmentHistory,
            total: data.getFHIRAppointmentByRequestPatientCodexId.total,
          });
        } else {
          setData({
            appointments: [],
            total: 0,
          });
        }
      },
    });

  useGetAppointmentsByPatientCodexIdQuery({
    variables: {
      appointmentStatus: historyStatuses,
      limit: LIST_BATCH_SIZE,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setAllAmount(data.getFHIRAppointmentByRequestPatientCodexId.total);
    },
    onError: () => {
      setAllAmount(0);
    },
  });

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    loggedUser?.uuid,
  );

  const value: AppointmentHistoryContextType = {
    data: data.appointments,
    total: data.total,
    variables: vars,
    loading: loading || dataLoading,
    setVars,
    allAmount,
  };

  return (
    <AppointmentHistoryContext.Provider value={value}>
      {children}
    </AppointmentHistoryContext.Provider>
  );
};

const ProviderAppointmentHistoryProvider: React.FC<{
  children: React.ReactNode;
  patientCodexId?: string;
  offset?: number;
  handleMyVisitSummaryClick: (appointmentId: string) => void;
  handleNavigateCompleteSummaryModal?: (appointmentId: string) => void;
}> = ({
  children,
  patientCodexId,
  handleMyVisitSummaryClick,
  offset,
  handleNavigateCompleteSummaryModal,
}) => {
  const { loading, locale } = useMyAppointmentsLocale();
  const { user: loggedUser } = useAuth();
  const [data, setData] = useState<dataType>({
    appointments: [],
    total: 0,
  });
  const [vars, setVars] = useState<varsType>({
    limit: 10,
    offset: offset || 0,
    filter: {
      sort: SORT_FHIR_APPOINTMENT_BY.DATE,
      sortDirection: ORDER_BY_DIRECTION_NUMERIC.DESC,
    },
  });

  const getFormatedToDate = () => {
    if (vars.filter.to) {
      const toDate = new Date(vars.filter.to);
      toDate.setHours(23, 59, 59, 999);
      return toDate.toISOString();
    } else {
      return undefined;
    }
  };

  const getFormatedFromDate = () => {
    if (vars.filter.from) {
      const fromDate = new Date(vars.filter.from);
      fromDate.setHours(0, 0, 0, 0);
      return fromDate.toISOString();
    } else {
      return undefined;
    }
  };

  const { fetchFileFromStorage } = useGetFileFromStorage();

  const getTestKitById =
    (patientId: string) => async (diagnosticReportId?: string) => {
      if (!diagnosticReportId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      window.open(pdfLink, '_blank');
    };

  const { loading: dataLoading, refetch } = useGetAppointmentsByProviderQuery({
    skip: !(loggedUser?.mfaPhoneNumber || loggedUser?.phoneNumber),
    variables: {
      patientCodexId,
      appointmentStatus: historyStatuses,
      ...vars.filter,
      to: getFormatedToDate(),
      from: getFormatedFromDate(),
      patientName: vars.filter.patient,
      limit: vars.limit,
      offset: vars.offset,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIRAppointmentByRequestProviderId.total > 0) {
        const allAppointmentHistory: AppointmentHistoryRowProps[] =
          data.getFHIRAppointmentByRequestProviderId.appointment.map(
            (appointment): AppointmentHistoryRowProps => ({
              id: appointment.appointmentCodexId,
              date: formatDateToCustomFormat(appointment.start || ''),
              end: formatDateToCustomFormat(appointment.end || ''),
              name: `${appointment.patientFirstName} ${appointment.patientLastName}`,
              patientName: `${appointment.patientFirstName} ${appointment.patientLastName}`,
              type: appointment.appointmentType || AppointmentTypeEnum.Chat,
              patientCodexId: appointment.codexPatientId,
              diagnosticReports: appointment.diagnosticReports || [],
              appointmentStatus:
                appointment.appointmentStatus as AppointmentHasuraStatus,
              status: appointment.status as AppointmentStatus,
              providerCodexId: appointment.codexProviderId as string,
              logs: generateAppointmentLogs(
                (appointment?.events ?? []) as AppointmentEvent[],
                (locale?.events ?? {}) as unknown as Record<string, string>,
              ),
              actions: [
                {
                  text: 'summary',
                  onClick: () =>
                    handleMyVisitSummaryClick(appointment.appointmentCodexId),
                },
                {
                  text: 'completeSummary',
                  onClick: () =>
                    handleNavigateCompleteSummaryModal &&
                    handleNavigateCompleteSummaryModal(
                      appointment.appointmentCodexId,
                    ),
                },
                {
                  text: 'testKits',
                  onClick: (id?: string) =>
                    getTestKitById(appointment.codexPatientId)(id),
                },
              ],
              summaryCompleted: appointment.isSummaryCompleted,
            }),
          );
        setData({
          appointments: allAppointmentHistory,
          total: data.getFHIRAppointmentByRequestProviderId.total,
        });
      } else {
        setData({
          appointments: [],
          total: 0,
        });
      }
    },
  });

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    loggedUser?.uuid,
  );

  const value: AppointmentHistoryContextType = {
    data: data.appointments,
    total: data.total,
    variables: vars,
    loading: loading || dataLoading,
    setVars,
  };

  return (
    <AppointmentHistoryContext.Provider value={value}>
      {children}
    </AppointmentHistoryContext.Provider>
  );
};

export const AppointmentHistoryProvider: React.FC<{
  children: React.ReactNode;
  patientCodexId?: string | undefined;
  handleMyVisitSummaryClick: (appointmentId: string) => void;
  handleNavigateCompleteSummaryModal?: (appointmentId: string) => void;
}> = ({
  children,
  patientCodexId,
  handleMyVisitSummaryClick,
  handleNavigateCompleteSummaryModal,
}) => {
  const { user } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get(appointmentOffsetParams);
  const userRole = user?.role;

  const Provider =
    userRole === Roles.PROVIDER
      ? ProviderAppointmentHistoryProvider
      : PatientAppointmentHistoryProvider;

  return (
    <Provider
      patientCodexId={patientCodexId}
      offset={Number(offset) || 0}
      handleMyVisitSummaryClick={handleMyVisitSummaryClick}
      handleNavigateCompleteSummaryModal={handleNavigateCompleteSummaryModal}
    >
      {children}
    </Provider>
  );
};

export const appointmentOffsetParams = 'pastAppointmentsOffset';
